import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import { Box, type SxProps, type Theme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { FONT_SIZE, INK, LETTER_SPACING, MOTION, NEUTRAL, SHEET_STEP } from "@/theme/tokens";
import SystemButton from "../SystemButton/SystemButton";
import { useSheetBack } from "./SheetBackContext";
import { useSheetDragHandle } from "./SheetDragHandleContext";

type Props = {
	/** The step's question or subject — "Why is Uria off?", a person's name. Already translated. */
	title: ReactNode;
	/** One quiet line of context under the title: which shift, which day, what this step changes. */
	subtitle?: ReactNode;
	/** Drawn before the title — a role badge for a person, a job chip for a shift. */
	leading?: ReactNode;
	/**
	 * Controls that belong with the question and must stay in reach while the body scrolls — a search field over a
	 * list. Pinned inside the heading, under the title, rather than stuck separately (two sticky layers at `top: 0`
	 * would slide one under the other).
	 */
	toolbar?: ReactNode;
	/** The step's content. Unpadded, so action rows run edge to edge; pad form content yourself. */
	children: ReactNode;
	/**
	 * The controls that end the step (Back, Cancel, the confirm), as `SheetStepButton`s so every step's footer carries the
	 * same density and emphasis. Pinned to the bottom of the sheet while it scrolls.
	 */
	footer?: ReactNode;
	/**
	 * Identifies the step. Changing it replays the body's entrance, which is how a sheet that swaps its content in
	 * place tells the eye "this is the next page" without closing and re-opening.
	 */
	stepKey: string;
	/** On the step root; the body gets `{dataTestId}-body` and the footer `{dataTestId}-footer`. */
	dataTestId?: string;
	bodySx?: SxProps<Theme>;
};

/**
 * One step of a multi-step bottom sheet: a sticky heading, the body, and a sticky footer, laid out to live inside
 * `InHouseMobileSheet` with `showHeader={false}` and `contentSx={{ p: 0 }}`.
 *
 * It exists so a flow can move through several steps — pick an action, pick a reason, confirm, see the result —
 * inside ONE sheet. Opening a new drawer per step replays the full slide-in and flashes the scrim each time; swapping
 * steps inside a sheet that stays open reads as the same surface turning a page. That also means the footer has to
 * be part of the step (each step has its own buttons), so it sticks to the bottom of the sheet's scroller here rather
 * than living in the sheet's `footer` slot.
 *
 * The heading is sticky for the same reason the footer is: on a long step (a list of units, a staff search) the
 * question the list answers must not scroll away.
 */
export const SheetStep: FC<Props> = ({ title, subtitle, leading, toolbar, children, footer, stepKey, dataTestId, bodySx }) => {
	// Inside a drag-to-close sheet the heading drags it too, as its handle does; anywhere else it is just a heading.
	const dragHandle = useSheetDragHandle();
	// Opened as a full page, the heading leads with ‹ back to the screen underneath, as the design's pages do.
	const back = useSheetBack();
	return (
		// At least the sheet's height, so in a fixed-height sheet a short step's footer sits on the sheet's bottom edge rather
		// than mid-sheet; in a sheet sized to its content the percentage resolves to nothing and the step stays its own size.
		<Box data-testid={dataTestId} sx={{ display: "flex", flexDirection: "column", minHeight: "100%" }}>
			<Box
				component="header"
				{...(dragHandle === null ? {} : dragHandle)}
				sx={{
					position: "sticky",
					top: 0,
					zIndex: 2,
					px: 2,
					pt: 2,
					pb: 1.5,
					bgcolor: "background.paper",
					borderBottom: `1px solid ${NEUTRAL[150]}`,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
					{back !== null && (
						<SystemButton
							buttonVariant="borderless"
							size="compact"
							iconButton
							noFullWidthMobile
							touchTarget
							onClick={back.onBack}
							aria-label={back.label}
							data-testid={dataTestId === undefined ? undefined : `${dataTestId}-back`}
							sx={{ ml: -1, color: INK.SECONDARY }}
						>
							<ChevronLeftRoundedIcon />
						</SystemButton>
					)}
					{leading}
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Box
							component="h2"
							sx={{
								m: 0,
								fontSize: FONT_SIZE.TITLE_SM,
								fontWeight: 700,
								lineHeight: 1.3,
								letterSpacing: LETTER_SPACING.HEADLINE,
								color: INK.PRIMARY,
								overflowWrap: "anywhere",
							}}
						>
							{title}
						</Box>
						{subtitle !== undefined && (
							<Box component="p" sx={{ m: 0, mt: 0.25, fontSize: FONT_SIZE.CAPTION, lineHeight: 1.4, color: INK.MUTED, overflowWrap: "anywhere" }}>
								{subtitle}
							</Box>
						)}
					</Box>
				</Box>
				{toolbar !== undefined && <Box sx={{ mt: 1.25 }}>{toolbar}</Box>}
			</Box>
			<Box
				// Keyed by step so the entrance replays on every step change, and only then.
				key={stepKey}
				data-testid={dataTestId === undefined ? undefined : `${dataTestId}-body`}
				sx={[
					{
						"@keyframes sheetStepEnter": {
							from: { opacity: 0, transform: `translateY(${SHEET_STEP.ENTER_RISE_PX}px)` },
							to: { opacity: 1, transform: "translateY(0)" },
						},
						animation: `sheetStepEnter ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
						// Movement is dropped under reduced motion; the fade stays, so the swap is still announced visually.
						"@keyframes sheetStepFade": { from: { opacity: 0 }, to: { opacity: 1 } },
						"@media (prefers-reduced-motion: reduce)": { animation: `sheetStepFade ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}` },
					},
					...(Array.isArray(bodySx) ? bodySx : [bodySx]),
				]}
			>
				{children}
			</Box>
			{footer !== undefined && (
				<Box
					component="footer"
					data-testid={dataTestId === undefined ? undefined : `${dataTestId}-footer`}
					sx={{
						position: "sticky",
						bottom: 0,
						zIndex: 2,
						mt: "auto",
						display: "flex",
						gap: 1,
						// Tight on purpose: the footer stays on screen under every step, so every pixel it takes is one the step's
						// content never gets back. `pb` is a notch deeper than `pt` so the buttons sit off the sheet's bottom edge.
						px: 2,
						pt: 1,
						pb: 1.5,
						bgcolor: "background.paper",
						borderTop: `1px solid ${NEUTRAL[150]}`,
					}}
				>
					{footer}
				</Box>
			)}
		</Box>
	);
};
